import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react';
import {
  type ApiClient,
  createApiClient,
  getStoredToken,
  setStoredRefreshHandle,
  setStoredToken,
  silentRefresh,
} from './api-client.js';
import { startPopupAuth } from './popup-oauth.js';

/**
 * Customer JWT claims as the SPA cares about them. The `sub` claim is
 * `wf-user:<workfrontUserId>`; the SPA strips the prefix for display.
 */
export interface CustomerSession {
  licenseId: string;
  clientId: string;
  workfrontUserId: string;
  email?: string;
  roles: readonly string[];
  /** Unix-seconds; SPA reads to decide stale state without calling app-api. */
  exp: number;
}

interface AuthContextValue {
  session: CustomerSession | null;
  api: ApiClient;
  /** Trigger popup OAuth — must be called from a user gesture. */
  signIn: (licenseId: string) => Promise<void>;
  signOut: () => Promise<void>;
}

const AuthContext = createContext<AuthContextValue | null>(null);

interface AuthProviderProps {
  /** App-API base URL — defaults to `import.meta.env.VITE_APP_API_BASE_URL`. */
  baseUrl?: string;
  children: ReactNode;
}

/**
 * Decodes a JWT payload without verifying the signature. The SPA does
 * NOT trust these claims for security decisions — app-api re-verifies
 * on every request via the AGW JWT authorizer. This is purely for
 * showing claim-derived UI state (license id, email) before the next
 * API roundtrip.
 */
function decodeJwt(token: string): CustomerSession | null {
  try {
    const [, payloadB64] = token.split('.');
    if (!payloadB64) return null;
    const payload = JSON.parse(atob(payloadB64.replace(/-/g, '+').replace(/_/g, '/'))) as Record<
      string,
      unknown
    >;
    if (
      typeof payload.licenseId !== 'string' ||
      typeof payload.clientId !== 'string' ||
      typeof payload.sub !== 'string' ||
      typeof payload.exp !== 'number'
    ) {
      return null;
    }
    const sub = payload.sub.startsWith('wf-user:')
      ? payload.sub.slice('wf-user:'.length)
      : payload.sub;
    return {
      licenseId: payload.licenseId,
      clientId: payload.clientId,
      workfrontUserId: sub,
      email: typeof payload.wfUserEmail === 'string' ? payload.wfUserEmail : undefined,
      roles: Array.isArray(payload.roles) ? (payload.roles as string[]) : [],
      exp: payload.exp,
    };
  } catch {
    return null;
  }
}

/**
 * Pre-expiry buffer for the proactive refresh timer. Five minutes
 * lets a single failed refresh retry twice before the JWT actually
 * expires, and avoids the popup-sign-in path during normal use.
 */
const PROACTIVE_REFRESH_LEAD_MS = 5 * 60 * 1000;

/**
 * Stale window for on-focus refresh: when the operator tabs back
 * in, anything within this window of expiry gets a pre-emptive
 * refresh so the next API call doesn't fire a 401 → silent-refresh
 * → retry hop.
 */
const FOCUS_REFRESH_STALE_MS = 60 * 1000;

export function AuthProvider({ baseUrl, children }: AuthProviderProps): JSX.Element {
  const resolvedBaseUrl =
    baseUrl ?? import.meta.env.VITE_APP_API_BASE_URL ?? 'https://app-api.workfocus.dev';
  const [session, setSession] = useState<CustomerSession | null>(() => {
    const token = getStoredToken();
    return token ? decodeJwt(token) : null;
  });

  const applyToken = useCallback((token: string | null) => {
    if (token === null) {
      setStoredToken(null);
      setSession(null);
      return;
    }
    setStoredToken(token);
    setSession(decodeJwt(token));
  }, []);

  const onAuthRequired = useCallback(() => {
    applyToken(null);
  }, [applyToken]);

  const api = useMemo(
    () => createApiClient({ baseUrl: resolvedBaseUrl, onAuthRequired }),
    [resolvedBaseUrl, onAuthRequired],
  );

  /**
   * Mount-time silent-refresh. If sessionStorage is empty (tab close,
   * fresh tab, sessionStorage disabled in incognito), but the
   * `__Host-` refresh cookie is still valid, mint a new JWT before
   * `RequireAuth` shows the popup-sign-in prompt. This is what makes
   * "session expires too fast" go away in practice — sessionStorage
   * clears on tab close, but the refresh cookie lives for 30 days.
   */
  useEffect(() => {
    if (session) return;
    let cancelled = false;
    void (async () => {
      const fresh = await silentRefresh(resolvedBaseUrl);
      if (cancelled) return;
      if (fresh) applyToken(fresh);
    })();
    return () => {
      cancelled = true;
    };
    // Intentionally only depend on baseUrl — we want one attempt per
    // (provider, baseUrl), not per `session` update. If `applyToken`
    // races with the resolved token, the most-recent token wins.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [resolvedBaseUrl]);

  /**
   * Auto-clear stale session if exp is in the past — the api-client
   * would also do it on the next 401, but doing it eagerly avoids a
   * failing first render.
   */
  useEffect(() => {
    if (session && session.exp * 1000 < Date.now()) {
      applyToken(null);
    }
  }, [session, applyToken]);

  /**
   * Proactive refresh — schedule a silent-refresh at `exp − 5 min` so
   * the JWT renews while the operator is still working. Cleared on
   * unmount or whenever a new session lands.
   */
  useEffect(() => {
    if (!session) return;
    const nowMs = Date.now();
    const expMs = session.exp * 1000;
    const refreshAt = expMs - PROACTIVE_REFRESH_LEAD_MS;
    const delay = Math.max(0, refreshAt - nowMs);
    if (delay <= 0) return; // covered by the stale-on-mount effect
    const timer = window.setTimeout(() => {
      void (async () => {
        const fresh = await silentRefresh(resolvedBaseUrl);
        if (fresh) applyToken(fresh);
      })();
    }, delay);
    return () => window.clearTimeout(timer);
  }, [session, resolvedBaseUrl, applyToken]);

  /**
   * Refresh on tab focus when the JWT is missing or within the stale
   * window of expiry. Operators commonly leave the iframe tab in the
   * background for hours — this catches the case where the proactive
   * timer would have fired while the tab was hidden.
   */
  useEffect(() => {
    function onFocus(): void {
      const expMs = session ? session.exp * 1000 : 0;
      const needsRefresh = !session || expMs - Date.now() < FOCUS_REFRESH_STALE_MS;
      if (!needsRefresh) return;
      void (async () => {
        const fresh = await silentRefresh(resolvedBaseUrl);
        if (fresh) applyToken(fresh);
      })();
    }
    window.addEventListener('focus', onFocus);
    return () => window.removeEventListener('focus', onFocus);
  }, [session, resolvedBaseUrl, applyToken]);

  const signIn = useCallback(
    async (licenseId: string) => {
      const result = await startPopupAuth({ baseUrl: resolvedBaseUrl, licenseId });
      // The refresh handle is the post-CHIPS path for silent-refresh:
      // store it in localStorage so the SPA can send it as
      // `Authorization: Bearer …` to `/auth/silent-refresh` on the
      // next cold mount. Older app-api builds don't return a handle
      // — leave whatever's already stored in place so the legacy
      // cookie path still has a chance.
      if (result.refreshHandle) {
        setStoredRefreshHandle(result.refreshHandle);
      }
      applyToken(result.token);
    },
    [resolvedBaseUrl, applyToken],
  );

  const signOut = useCallback(async () => {
    try {
      await api.delete('/auth/session');
    } catch {
      /* best effort — local clear still happens */
    }
    setStoredRefreshHandle(null);
    applyToken(null);
  }, [api, applyToken]);

  const value = useMemo<AuthContextValue>(
    () => ({ session, api, signIn, signOut }),
    [session, api, signIn, signOut],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used within <AuthProvider>');
  return ctx;
}
